import type { KeyboardEvent } from "react";
import {
  DECIDUO_INFERIOR_DIREITO,
  DECIDUO_INFERIOR_ESQUERDO,
  DECIDUO_SUPERIOR_DIREITO,
  DECIDUO_SUPERIOR_ESQUERDO,
} from "@bookdental/schemas";

export type CondicaoDoDente = { preenchimento: string; contorno: string };

// Largura (ao longo da arcada) × altura de cada tipo, do central ao 2º molar.
const TAMANHO: [number, number][] = [
  [19, 22],
  [15, 20],
  [17, 22],
  [21, 22],
  [25, 24],
];
const RX = 58;
const RY = 76;
const FOLGA = 1.5;

type Dente = {
  fdi: string;
  x: number;
  y: number;
  giro: number;
  w: number;
  h: number;
};

/** Ângulo (graus, y pra baixo) a uma distância de arco `s` do meio da arcada, andando pra um lado. */
function anguloNoArco(s: number, inicio: number, sentido: 1 | -1) {
  let percorrido = 0;
  let a = inicio;
  let px = RX * Math.cos((a * Math.PI) / 180);
  let py = RY * Math.sin((a * Math.PI) / 180);
  while (percorrido < s) {
    a += 0.25 * sentido;
    const x = RX * Math.cos((a * Math.PI) / 180);
    const y = RY * Math.sin((a * Math.PI) / 180);
    percorrido += Math.hypot(x - px, y - py);
    px = x;
    py = y;
  }
  return a;
}

/** Os dentes encostados um no outro ao longo de meia elipse, do central pra trás. */
function posicoes(): Dente[] {
  const dentes: Dente[] = [];
  const quadrante = (lista: string[], cx: number, cy: number, inicio: number, sentido: 1 | -1) => {
    let s = 0;
    // Listas vêm de trás pra frente (55→51); a posição anda do central (51) pro 2º molar (55).
    [...lista].reverse().forEach((fdi, i) => {
      const [w, h] = TAMANHO[i];
      const centro = s + w / 2;
      s += w + FOLGA;
      const graus = anguloNoArco(centro, inicio, sentido);
      const t = (graus * Math.PI) / 180;
      const x = cx + RX * Math.cos(t);
      const y = cy + RY * Math.sin(t);
      const giro = (Math.atan2(RY * Math.cos(t), -RX * Math.sin(t)) * 180) / Math.PI;
      dentes.push({ fdi, x, y, giro, w, h });
    });
  };
  // Superior: meio no topo (270°); o lado direito do paciente fica à esquerda da tela.
  quadrante(DECIDUO_SUPERIOR_DIREITO, 100, 112, 270 - 0.01, -1);
  quadrante([...DECIDUO_SUPERIOR_ESQUERDO].reverse(), 100, 112, 270 + 0.01, 1);
  // Inferior: meio embaixo (90°).
  quadrante(DECIDUO_INFERIOR_DIREITO, 100, 138, 90 + 0.01, 1);
  quadrante([...DECIDUO_INFERIOR_ESQUERDO].reverse(), 100, 138, 90 - 0.01, -1);
  return dentes;
}
const DENTES = posicoes();

const AZUL_ESCURO = "#3e5edc";
const AZUL_CLARO = "#c6ccf8";
const CONTORNO = "#8a98be";

/**
 * Arcada decídua (51–85) desenhada por nós — a biblioteca do odontograma só tem a permanente. Mesmo
 * traço e cores da arcada adulta; cada dente é um botão acessível (Tab, Enter/Espaço). O número do dente aparece no tooltip de
 * `Arcada`, igual ao da permanente.
 */
export function ArcadaDecidua({
  selecionados,
  condicoes,
  corDaSelecao,
  somenteLeitura = false,
  onAlternar,
}: {
  selecionados: string[];
  condicoes?: Map<string, CondicaoDoDente>;
  corDaSelecao?: string | null;
  somenteLeitura?: boolean;
  onAlternar?: (fdi: string) => void;
}) {
  const teclado = (e: KeyboardEvent, fdi: string) => {
    if (somenteLeitura || (e.key !== "Enter" && e.key !== " ")) return;
    e.preventDefault();
    onAlternar?.(fdi);
  };
  return (
    <svg viewBox="0 0 200 250" className="h-auto w-full select-none" aria-label="Arcada decídua">
      {DENTES.map((d) => {
        const marcado = selecionados.includes(d.fdi);
        const cond = condicoes?.get(d.fdi);
        const preenchimento = marcado
          ? corDaSelecao
            ? `${corDaSelecao}66`
            : AZUL_CLARO
          : (cond?.preenchimento ?? "#ffffff");
        const contorno = marcado ? (corDaSelecao ?? AZUL_ESCURO) : (cond?.contorno ?? CONTORNO);
        return (
          <g
            key={d.fdi}
            role={somenteLeitura ? "img" : "button"}
            tabIndex={somenteLeitura ? -1 : 0}
            aria-label={`Dente ${d.fdi}`}
            aria-pressed={somenteLeitura ? undefined : marcado}
            onClick={somenteLeitura ? undefined : () => onAlternar?.(d.fdi)}
            onKeyDown={(e) => teclado(e, d.fdi)}
            data-dente={d.fdi}
            className={somenteLeitura ? undefined : "cursor-pointer outline-none [&:focus-visible>rect]:stroke-[2.5]"}
          >
            <rect
              x={d.x - d.w / 2}
              y={d.y - d.h / 2}
              width={d.w}
              height={d.h}
              rx={6}
              transform={`rotate(${d.giro} ${d.x} ${d.y})`}
              fill={preenchimento}
              stroke={contorno}
              strokeWidth={marcado ? 1.8 : 1.2}
              className="transition-colors"
            />
          </g>
        );
      })}
    </svg>
  );
}
